import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './YearFilter.scss';

interface YearFilterProps {
    onYearChange: (year: string) => void;
    initialYear?: string;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    className?: string;
    minYear?: number;
    maxYear?: number;
    loading?: boolean;
}

export const YearFilter: React.FC<YearFilterProps> = ({
    onYearChange,
    initialYear = new Date().getFullYear().toString(),
    placeholder = "YYYY",
    label,
    disabled = false,
    className = "",
    minYear = 1980,
    maxYear = new Date().getFullYear(),
    loading = false
}) => {
    const { t } = useTranslation();
    const [year, setYear] = useState(initialYear);
    const [showYearPicker, setShowYearPicker] = useState(false);
    const yearPickerRef = useRef<HTMLDivElement>(null);
    const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastLoadedYearRef = useRef<string>('');

    // Generate array of years
    const yearOptions = useMemo(() => {
        return Array.from(
            { length: maxYear - minYear + 1 },
            (_, i) => maxYear - i
        );
    }, [minYear, maxYear]);

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newYear = e.target.value;
        setYear(newYear);
    };

    const handleLoadClick = () => {
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
        }
        if (lastLoadedYearRef.current !== year) {
            lastLoadedYearRef.current = year;
            onYearChange(year);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleLoadClick();
        }
    };

    const handleYearSelect = (selectedYear: number) => {
        const yearString = selectedYear.toString();
        setYear(yearString);
        setShowYearPicker(false);
        
        // Debounce to prevent duplicate calls
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
        }
        
        if (lastLoadedYearRef.current !== yearString) {
            lastLoadedYearRef.current = yearString;
            loadTimeoutRef.current = setTimeout(() => {
                onYearChange(yearString);
            }, 100);
        }
    };

    const handleCalendarIconClick = () => {
        if (!disabled && !loading) {
            setShowYearPicker(!showYearPicker);
        }
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (loadTimeoutRef.current) {
                clearTimeout(loadTimeoutRef.current);
            }
        };
    }, []);

    // Close year picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (yearPickerRef.current && !yearPickerRef.current.contains(event.target as Node)) {
                setShowYearPicker(false);
            }
        };

        if (showYearPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showYearPicker]);

    return (
        <div className={`year-filter ${className} ${loading ? 'year-filter--loading' : ''}`}>
            <div className="year-filter__input-section">
                {label && (
                    <label className="year-filter__label">
                        {label}
                    </label>
                )}
                <div className="year-filter__input-wrapper" ref={yearPickerRef}>
                    <input
                        type="number"
                        value={year}
                        onChange={handleYearChange}
                        onKeyPress={handleKeyPress}
                        placeholder={placeholder}
                        min={minYear}
                        max={maxYear}
                        disabled={disabled || loading}
                        className="year-filter__input"
                    />
                    <div 
                        className="year-filter__calendar-icon"
                        onClick={handleCalendarIconClick}
                        style={{ cursor: (disabled || loading) ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                d="M6.66699 1.66666V4.99999M13.3337 1.66666V4.99999M2.50033 8.33332H17.5003M4.16699 3.33332H15.8337C16.7541 3.33332 17.5003 4.07952 17.5003 4.99999V16.6667C17.5003 17.5871 16.7541 18.3333 15.8337 18.3333H4.16699C3.24652 18.3333 2.50033 17.5871 2.50033 16.6667V4.99999C2.50033 4.07952 3.24652 3.33332 4.16699 3.33332Z"
                                stroke="currentColor"
                                strokeWidth="1.67"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    {loading && (
                        <div className="year-filter__loader">
                            <div className="year-filter__spinner"></div>
                        </div>
                    )}
                    {showYearPicker && (
                        <div className="year-filter__year-dropdown">
                            <div className="year-filter__year-header">
                                <span>{t('SELECT_YEAR') || 'Select Year'}</span>
                                <button
                                    type="button"
                                    className="year-filter__year-close"
                                    onClick={() => setShowYearPicker(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="year-filter__year-list">
                                {yearOptions.map((yearOption) => (
                                    <button
                                        key={yearOption}
                                        type="button"
                                        className={`year-filter__year-option ${year === yearOption.toString() ? 'active' : ''}`}
                                        onClick={() => handleYearSelect(yearOption)}
                                    >
                                        {yearOption}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* <button
                onClick={handleLoadClick}
                disabled={disabled || !year}
                className="year-filter__load-btn"
                type="button"
            >
                {t('LOAD') || 'Load'}
            </button> */}
        </div>
    );
};